import { Link } from 'react-router'
import { useBahasa } from '../../../app/i18n'
import { useProspek } from '../../../domains/prospect/api'
import { useJadwalSaya } from '../../../domains/meeting/api'
import { useAntreanKonten } from '../../../domains/content/api'
import { useAntrean } from '../../../domains/conversation/api'
import { tanggal, tanggalJam } from '../../../shared/lib/waktu'
import { Body, Gagal, KepalaHalaman, Kosong, Memuat, Panel, Tombol } from '../../../shared/ui'

function Tugas({ judul, keterangan, ke, aksi }) {
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 items-center bg-surface border border-line px-3.5 py-3 first:rounded-t-md last:rounded-b-md [&+li]:border-t-0">
      <div className="min-w-0">
        <b className="text-[12.5px] font-semibold">{judul}</b>
        {keterangan && <span className="block text-[11.5px] text-ink3">{keterangan}</span>}
      </div>
      {ke ? (
        <Link to={ke}>
          <Tombol jenis="hantu">{aksi}</Tombol>
        </Link>
      ) : null}
    </li>
  )
}

/**
 * Beranda staf adalah DAFTAR TUGAS, bukan dasbor angka.
 *
 * Orang ini membuka aplikasi seminggu sekali; yang ia butuhkan cuma "apa yang
 * tertahan menunggu saya". Angka tetap ada, tapi DI BAWAH daftar tugas — bukan
 * di atasnya.
 *
 * Satu tugas yang belum bisa dihitung: DM yang menunggu dijawab. Rutenya belum
 * ada (lihat PerluDijawab.jsx), jadi barisnya tidak dikarang — ia tidak muncul
 * sama sekali sampai angkanya benar-benar bisa dibaca.
 */
export default function HariIni() {
  const { t, locale } = useBahasa()
  const prospek = useProspek()
  const meeting = useJadwalSaya()
  const konten = useAntreanKonten()
  const antrean = useAntrean({ status: 'pending', diambil: 'belum', limit: 1 })

  const memuat = prospek.isLoading || meeting.isLoading || konten.isLoading || antrean.isLoading
  const galat = prospek.error || meeting.error || konten.error || antrean.error

  const daftarKonten = Array.isArray(konten.data) ? konten.data : konten.data?.items ?? []
  const menunggu = daftarKonten.filter((k) => k.status === 'pending_approval')
  const daftarProspek = Array.isArray(prospek.data) ? prospek.data : prospek.data?.items ?? []
  const belumLengkap = daftarProspek.filter((p) => p.stage === 'collecting')
  const daftarMeeting = Array.isArray(meeting.data) ? meeting.data : []
  const hariIni = tanggal(new Date(), locale)
  const meetingHariIni = daftarMeeting.filter((m) => tanggal(m.confirmed_at, locale) === hariIni)
  const meetingBerikut = meetingHariIni[0] || daftarMeeting.find((m) => new Date(m.confirmed_at) >= new Date())

  const belumDijawab = antrean.data?.total ?? 0

  const tugas = [
    belumDijawab && {
      judul: t('hariIni.dmMenunggu', { n: belumDijawab }),
      keterangan: 'Bot menyerah dan menyerahkannya ke tim.',
      ke: '/perlu-dijawab',
      aksi: t('umum.lihat'),
    },
    menunggu.length && {
      judul: t('hariIni.kontenMenunggu', { n: menunggu.length }),
      keterangan: 'Kalau tidak ditinjau, slot terbitnya terlewat.',
      ke: '/konten',
      aksi: t('umum.lihat'),
    },
    meetingBerikut && {
      judul: `${meetingHariIni.length ? t('hariIni.meetingHariIni', { n: meetingHariIni.length }) : t('hariIni.meetingBerikut')} — ${meetingBerikut.company_name ?? '—'}`,
      keterangan: tanggalJam(meetingBerikut.confirmed_at ?? meetingBerikut.date, locale),
      ke: '/meeting',
      aksi: t('umum.buka'),
    },
    belumLengkap.length && {
      judul: `${belumLengkap.length} prospek datanya belum lengkap`,
      keterangan: 'Bot masih mengumpulkan; belum bisa ditawari meeting.',
      ke: '/prospek',
      aksi: t('umum.lihat'),
    },
  ].filter(Boolean)

  return (
    <>
      <KepalaHalaman judul={t('hariIni.judul')} keterangan={t('hariIni.keterangan')} />

      {memuat && <Memuat tinggi={4} />}
      {galat && !memuat && <Gagal galat={galat} ulangi={() => { prospek.refetch(); meeting.refetch(); konten.refetch(); antrean.refetch() }} />}

      {!memuat && !galat && (
        tugas.length ? (
          <ul className="m-0 p-0 flex flex-col gap-px">
            {tugas.map((x, i) => <Tugas key={i} {...x} />)}
          </ul>
        ) : (
          <Panel><Kosong judul={t('hariIni.bersih')} /></Panel>
        )
      )}

      {!memuat && !galat && (
        <Panel judul={t('hariIni.angka7hari')} catatan="angka, bukan tugas — sengaja di bawah">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(126px,1fr))] gap-px bg-line">
            {[
              [t('nav.perluDijawab'), belumDijawab],
              [t('hariIni.prospekBaru'), daftarProspek.length],
              [t('nav.meeting'), daftarMeeting.length],
              [t('hariIni.kontenTerbit'), daftarKonten.filter((k) => k.status === 'published').length],
            ].map(([k, v]) => (
              <div key={k} className="bg-surface px-3.5 py-3 flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold uppercase tracking-[.08em] text-ink3">{k}</span>
                <span className="font-mono text-[21px] font-medium tabular leading-tight">{v}</span>
              </div>
            ))}
          </div>
          <Body className="border-t border-line">
            <p className="m-0 text-[11.5px] text-ink3">
              Angka tujuh hari belum tersaring per tanggal — rutenya belum menerima rentang waktu,
              jadi yang tampil adalah jumlah keseluruhan. Ditandai supaya tidak dibaca sebagai mingguan.
            </p>
          </Body>
        </Panel>
      )}
    </>
  )
}
